import { useState } from 'react';
import type { Correlation, Question } from './types';
import { titleText } from './types';

type Props = {
  /** 当前题的 correlation 数组；undefined 或空数组代表未配置 */
  correlation: Correlation[] | undefined;
  allQuestions: Question[];
  currentQid: string;
  onChange: (correlation: Correlation[] | undefined) => void;
  onClose: () => void;
};

/** matrixtext 行控件类型：text=文本输入框 / select=下拉选择框（与 types.ts MatrixRowControl.kind 一致） */
type RowKind = 'text' | 'select';
const OP_KINDS: Array<{ value: 'equals' | 'contains' | 'isNotEmpty' | 'isEmpty'; label: string }> = [
  { value: 'equals', label: '等于' },
  { value: 'contains', label: '包含' },
  { value: 'isNotEmpty', label: '不为空' },
  { value: 'isEmpty', label: '为空' },
];

/**
 * 1627 新增「题目关联」弹窗：简化版显示条件。
 *  - 每条规则 = { questionId, optionValue[, row] } 或 { questionId, row, op[, matchText] }：
 *    - 前置题选中指定选项时，才显示当前题
 *    - 矩阵题多带 row 字段：触发条件 = 矩阵题答案 answers[questionId][row] === optionValue
 *    - 矩阵填空题（matrixtext）下拉行：同上 optionValue 精确匹配
 *    - 矩阵填空题（matrixtext）文本行：用 op（equals/contains/isNotEmpty/isEmpty）+ 可选 matchText 做文本匹配
 *  - 多条规则之间是 OR 关系（任一命中即显示）
 *  - 引用题支持：single/multi/select/ranking（optionValue）、matrix（optionValue + row）、
 *    matrixtext（配 rowControls；按行 kind 决定 optionValue 或 op+matchText）
 *  - 与 displayLogic 是 AND 关系（displayLogic 也通过才最终显示）
 */
export default function QuestionCorrelationModal({ correlation, allQuestions, currentQid, onChange, onClose }: Props) {
  const [draft, setDraft] = useState<Correlation[]>(() =>
    (correlation ?? []).filter(
      (c) => c && typeof c.questionId === 'string' && (typeof c.optionValue === 'string' || typeof c.op === 'string'),
    ),
  );

  // 可引用：选择题（single/multi/select/ranking）、矩阵量表题（matrix，rows/columns 非空）、
  // 或矩阵填空题（matrixtext，有 rows 且配了 rowControls）
  const candidateQuestions = allQuestions.filter((q) => {
    if (q.id === currentQid) return false;
    if (q.type === 'matrix') {
      const rows: string[] = Array.isArray(q.params?.rows) ? q.params!.rows : [];
      const cols: string[] = Array.isArray(q.params?.columns) ? q.params!.columns : [];
      return rows.length > 0 && cols.length > 0;
    }
    if (q.type === 'matrixtext') {
      const rows: string[] = Array.isArray(q.params?.rows) ? (q.params!.rows as string[]).filter((v): v is string => typeof v === 'string' && v !== '') : [];
      const rc = (q.params as any)?.rowControls;
      return rows.length > 0 && !!rc && typeof rc === 'object' && !Array.isArray(rc);
    }
    return Array.isArray(q.options) && q.options.length > 0;
  });
  const candidateIds = new Set(candidateQuestions.map((q) => q.id));

  const update = (next: Correlation[]) => setDraft(next);
  const updateAt = (i: number, patch: Partial<Correlation>) => update(draft.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  /** matrixtext 某行的控件类型（rowControls[r].kind）；非 matrixtext 返回 undefined */
  function rowKindOf(q: Question | undefined, row: string): RowKind | undefined {
    if (!q || q.type !== 'matrixtext' || !row) return undefined;
    const rc = (q.params as any)?.rowControls;
    const ctrl = rc && typeof rc === 'object' ? rc[row] : null;
    if (ctrl && typeof ctrl === 'object' && ctrl.kind === 'select') return 'select';
    if (ctrl && typeof ctrl === 'object' && ctrl.kind === 'text') return 'text';
    return undefined;
  }

  /** 某题的「选项值」列表：选择题取 options；matrix 取 columns；matrixtext 文本行无选项；matrixtext 下拉行取该行 options */
  function optionValuesOf(q: Question | undefined, row?: string): string[] {
    if (!q) return [];
    if (q.type === 'matrix') {
      return Array.isArray(q.params?.columns)
        ? (q.params!.columns as string[]).filter((v): v is string => typeof v === 'string' && v !== '')
        : [];
    }
    if (q.type === 'matrixtext' && typeof row === 'string' && row !== '') {
      const rc = (q.params as any)?.rowControls;
      const ctrl = rc && typeof rc === 'object' ? rc[row] : null;
      if (ctrl && typeof ctrl === 'object' && ctrl.kind === 'select' && Array.isArray(ctrl.options)) {
        return ctrl.options.filter((v: unknown): v is string => typeof v === 'string' && v !== '');
      }
      return [];
    }
    return (q.options ?? [])
      .map((o) => (typeof o === 'string' ? o : (o as any).value))
      .filter((v): v is string => typeof v === 'string' && v !== '');
  }
  function rowValuesOf(q: Question | undefined): string[] {
    if (!q || (q.type !== 'matrix' && q.type !== 'matrixtext')) return [];
    return Array.isArray(q.params?.rows)
      ? (q.params!.rows as string[]).filter((v): v is string => typeof v === 'string' && v !== '')
      : [];
  }
  function pickFirstOptionValue(q: Question, row?: string): string {
    return optionValuesOf(q, row)[0] ?? '';
  }
  function pickFirstRow(q: Question): string {
    return rowValuesOf(q)[0] ?? '';
  }

  /** 按题型 + 行 kind 构造一条新规则 */
  function buildNewRule(target: Question): Correlation | null {
    if (target.type === 'matrixtext') {
      const firstRow = pickFirstRow(target);
      if (!firstRow) return null;
      const kind = rowKindOf(target, firstRow);
      if (kind === 'select') {
        return { questionId: target.id, row: firstRow, optionValue: pickFirstOptionValue(target, firstRow) };
      }
      return { questionId: target.id, row: firstRow, op: 'equals', matchText: '' };
    }
    if (target.type === 'matrix') {
      const firstRow = pickFirstRow(target);
      const next: Correlation = { questionId: target.id, optionValue: pickFirstOptionValue(target) };
      if (firstRow) next.row = firstRow;
      return next;
    }
    return { questionId: target.id, optionValue: pickFirstOptionValue(target) };
  }

  const addRule = () => {
    const target = candidateQuestions[0];
    if (!target) {
      alert('需要有至少 1 道选择题 / 矩阵量表题 / 矩阵填空题才能配置关联');
      return;
    }
    const next = buildNewRule(target);
    if (!next) {
      alert('该题缺少有效的行配置，无法新增关联');
      return;
    }
    update([...draft, next]);
  };
  const removeAt = (i: number) => update(draft.filter((_, idx) => idx !== i));

  /** 切换题型：重置 row / optionValue / op / matchText */
  function patchForNewQuestion(prev: Correlation, newQ: Question | undefined): Partial<Correlation> {
    const patch: Partial<Correlation> = { optionValue: undefined, op: undefined, matchText: undefined, row: undefined };
    if (!newQ) return patch;
    if (newQ.type === 'matrixtext') {
      const firstRow = pickFirstRow(newQ);
      if (firstRow) {
        patch.row = firstRow;
        const kind = rowKindOf(newQ, firstRow);
        if (kind === 'select') patch.optionValue = pickFirstOptionValue(newQ, firstRow);
        else { patch.op = 'equals'; patch.matchText = ''; }
      }
      return patch;
    }
    if (newQ.type === 'matrix') {
      const firstRow = pickFirstRow(newQ);
      if (firstRow) patch.row = firstRow;
    }
    patch.optionValue = pickFirstOptionValue(newQ, patch.row);
    return patch;
  }

  /** 切换行（仅 matrix/matrixtext）：重置 optionValue / op / matchText */
  function patchForNewRow(prev: Correlation, q: Question | undefined, newRow: string): Partial<Correlation> {
    const patch: Partial<Correlation> = { row: newRow, optionValue: undefined, op: undefined, matchText: undefined };
    if (!q || !newRow) return patch;
    if (q.type === 'matrixtext') {
      const kind = rowKindOf(q, newRow);
      if (kind === 'select') patch.optionValue = pickFirstOptionValue(q, newRow);
      else { patch.op = 'equals'; patch.matchText = ''; }
    } else if (q.type === 'matrix') {
      patch.optionValue = pickFirstOptionValue(q);
    }
    return patch;
  }

  const handleConfirm = () => {
    // 清理：去除引用题不存在 / 字段缺失 / 引用值不存在的脏数据
    const cleaned = draft.filter((c) => {
      if (!candidateIds.has(c.questionId)) return false;
      const q = candidateQuestions.find((x) => x.id === c.questionId);
      if (!q) return false;
      if (q.type === 'matrixtext') {
        const rows = rowValuesOf(q);
        if (typeof c.row !== 'string' || c.row === '' || !rows.includes(c.row)) return false;
        const kind = rowKindOf(q, c.row);
        if (kind === 'select') {
          const opts = optionValuesOf(q, c.row);
          return typeof c.optionValue === 'string' && opts.includes(c.optionValue);
        }
        // 文本行
        if (typeof c.op !== 'string' || !['equals', 'contains', 'isNotEmpty', 'isEmpty'].includes(c.op)) return false;
        if ((c.op === 'equals' || c.op === 'contains') && (typeof c.matchText !== 'string' || c.matchText === '')) return false;
        return true;
      }
      // 选择题 / matrix 量表
      const optValues = optionValuesOf(q);
      if (typeof c.optionValue !== 'string' || c.optionValue === '' || !optValues.includes(c.optionValue)) return false;
      if (q.type === 'matrix') {
        const rows = rowValuesOf(q);
        if (typeof c.row !== 'string' || c.row === '' || !rows.includes(c.row)) return false;
      } else if (c.row !== undefined && c.row !== null && c.row !== '') {
        return false;
      }
      return true;
    });
    onChange(cleaned.length ? cleaned : undefined);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>题目关联</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>

        <div className="modal-body">
          <div className="hint" style={{ marginBottom: 8 }}>
            选中目标题目后，配置「前置题目 + 该题下的选项/条件」：当填写者在前置题命中指定条件时，当前题目才显示；不满足则隐藏不渲染。多条规则之间是「任一命中即显示」关系。
            引用题支持：单选 / 多选 / 下拉 / 排序（按选项）、矩阵量表（按行+刻度列）、矩阵填空（按行；下拉行选选项、文本行用「等于/包含/不为空/为空」文本匹配）。
          </div>

          {draft.length === 0 ? (
            <div className="hint">尚未配置任何关联，点击下方按钮新增。</div>
          ) : (
            <div className="logic-conds">
              {draft.map((c, i) => {
                const targetQ = candidateQuestions.find((q) => q.id === c.questionId);
                const isMatrixSrc = targetQ?.type === 'matrix';
                const isMatrixTextSrc = targetQ?.type === 'matrixtext';
                const isMatrixFamily = isMatrixSrc || isMatrixTextSrc;
                const rowKind: RowKind | undefined = isMatrixTextSrc && c.row ? rowKindOf(targetQ, c.row) : undefined;
                const optValues = optionValuesOf(targetQ, c.row);
                const rowValues = rowValuesOf(targetQ);
                return (
                  <div key={i} className={`logic-row${isMatrixFamily ? ' is-matrix' : ''}`}>
                    {/* 前置题 */}
                    <select
                      className="input"
                      value={c.questionId}
                      onChange={(e) => {
                        const newQ = candidateQuestions.find((q) => q.id === e.target.value);
                        updateAt(i, { questionId: e.target.value, ...patchForNewQuestion(c, newQ) });
                      }}
                    >
                      <option value="">选前置题目</option>
                      {candidateQuestions.map((q) => (
                        <option key={q.id} value={q.id}>
                          {titleText(q.title) || q.id}
                          {q.type === 'matrix' ? '（矩阵量表）' : q.type === 'matrixtext' ? '（矩阵填空）' : ''}
                        </option>
                      ))}
                    </select>

                    <span className="jump-arrow" style={{ flex: 'none' }}>→</span>

                    {/* 矩阵行（matrix / matrixtext 都显示） */}
                    {isMatrixFamily ? (
                      <select
                        className="input logic-value"
                        value={c.row ?? ''}
                        onChange={(e) => updateAt(i, patchForNewRow(c, targetQ, e.target.value))}
                        disabled={rowValues.length === 0}
                      >
                        <option value="">选行</option>
                        {rowValues.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : null}

                    {/* matrixtext 文本行：运算符 + 文本框 */}
                    {isMatrixTextSrc && rowKind === 'text' ? (
                      <>
                        <select
                          className="input logic-op"
                          value={c.op ?? 'equals'}
                          onChange={(e) => updateAt(i, { op: e.target.value as 'equals' | 'contains' | 'isNotEmpty' | 'isEmpty' })}
                        >
                          {OP_KINDS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        {(c.op === 'equals' || c.op === 'contains' || !c.op) ? (
                          <input
                            className="input logic-value"
                            type="text"
                            value={c.matchText ?? ''}
                            placeholder="匹配文本"
                            onChange={(e) => updateAt(i, { matchText: e.target.value })}
                          />
                        ) : null}
                      </>
                    ) : (
                      /* 选项 / 量表刻度 / matrixtext 下拉行 */
                      <select
                        className="input logic-value"
                        value={c.optionValue ?? ''}
                        onChange={(e) => updateAt(i, { optionValue: e.target.value })}
                        disabled={!targetQ || optValues.length === 0}
                      >
                        <option value="">{isMatrixSrc ? '选刻度' : '选选项'}</option>
                        {optValues.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    )}

                    <button className="icon-btn icon-danger" onClick={() => removeAt(i)} title="删除规则">✕</button>
                  </div>
                );
              })}
            </div>
          )}

          <button className="qcard-link" onClick={addRule} disabled={candidateQuestions.length === 0}>
            + 添加关联规则
          </button>

          {candidateQuestions.length === 0 && (
            <div className="hint hint-bad">前面没有可关联的选择题（单选 / 多选 / 下拉 / 排序）、矩阵量表题或矩阵填空题。</div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={handleConfirm}>确定</button>
        </div>
      </div>
    </div>
  );
}
